import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Reveal } from "@/components/marketing/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = ["Parent", "Social Worker", "Agency / Caseworker", "Mediator"];

const inputCls =
  "w-full rounded-xl border border-ink/15 bg-paper px-4 py-3 text-sm placeholder:text-ink-light outline-none transition-all duration-300 focus:border-clay focus:ring-2 focus:ring-clay/20";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", role: "", message: "", team_size: "", caseload: "" });
  const [sending, setSending] = useState(false);
  const isAgency = form.role === "Agency / Caseworker";

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, role: e.detail }));
    window.addEventListener("dc:prefill-role", onPrefill);
    return () => window.removeEventListener("dc:prefill-role", onPrefill);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.role) {
      toast.error("Please choose your role.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll be in touch soon.");
      setForm({ name: "", email: "", role: "", message: "", team_size: "", caseload: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-paper-2 border-t border-ink/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-clay font-semibold">Contact Us</p>
          <h2 className="mt-5 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-tight">
            Let's talk about bonding time.
          </h2>
          <p className="mt-6 max-w-md text-base text-ink-muted leading-relaxed">
            Dual Care is a partnership between parents and social workers with one shared goal —
            more quality time between children and the people who love them. Questions, early
            access, or agency partnerships: a real human replies within one business day.
          </p>
          <div className="spotlight-frame mt-9 overflow-hidden rounded-3xl border border-ink/10 max-w-md shadow-[0_24px_50px_-24px_rgba(26,25,24,0.35)]" data-testid="contact-image-frame">
            <img
              src="https://images.unsplash.com/photo-1543333995-a78aea2eee50?q=85&w=1200&auto=format&fit=crop"
              alt="Caseworker sharing a warm moment with a family"
              className="aspect-[16/10] w-full object-cover"
              data-testid="contact-image"
            />
          </div>
          <div className="mt-8 flex items-center gap-3 text-sm text-ink-muted">
            <span className="flex -space-x-2">
              {["#C86D51", "#3A4F41", "#D4A359"].map((c) => (
                <span key={c} className="h-8 w-8 rounded-full border-2 border-paper-2" style={{ backgroundColor: c }} />
              ))}
            </span>
            <span data-testid="contact-social-proof">Trusted by families and caseworkers alike</span>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <form onSubmit={submit} className="rounded-3xl border border-ink/10 bg-paper p-8 sm:p-10 shadow-[0_30px_60px_-30px_rgba(26,25,24,0.25)]" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input
                required
                minLength={2}
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                data-testid="contact-name-input"
              />
              <input
                required
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                data-testid="contact-email-input"
              />
            </div>
            <div className="mt-4">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className={`${inputCls} h-auto`} data-testid="contact-role-select">
                  <SelectValue placeholder="I am a…" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r} data-testid={`contact-role-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isAgency && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="grid sm:grid-cols-2 gap-4 mt-4"
                data-testid="agency-fields"
              >
                <input
                  placeholder="Team size (e.g. 12 caseworkers)"
                  value={form.team_size}
                  onChange={(e) => setForm({ ...form, team_size: e.target.value })}
                  className={inputCls}
                  data-testid="contact-team-size-input"
                />
                <input
                  placeholder="Active families (e.g. 40)"
                  value={form.caseload}
                  onChange={(e) => setForm({ ...form, caseload: e.target.value })}
                  className={inputCls}
                  data-testid="contact-caseload-input"
                />
              </motion.div>
            )}
            <textarea
              required
              minLength={2}
              rows={4}
              placeholder="How can we help?"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} mt-4 resize-none`}
              data-testid="contact-message-input"
            />
            <motion.button
              type="submit"
              disabled={sending}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-clay py-3.5 font-medium text-paper transition-colors duration-300 hover:bg-clay-dark disabled:opacity-70"
              data-testid="contact-submit-button"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? "Sending…" : "Send Message"}
            </motion.button>
            <p className="mt-4 text-center text-[11px] font-mono text-ink-light">
              Every message lands directly with our team.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
